// Image helpers for bird photos.
// Photos are stored in the database as data URLs, so they must be downscaled
// and compressed before saving or the database balloons in size.

const MAX_DIMENSION = 1000;
const JPEG_QUALITY = 0.82;
const MAX_INPUT_BYTES = 20 * 1024 * 1024; // refuse absurdly large source files

/**
 * Read a Blob (or File) into a data URL.
 * @param {Blob} blob
 * @param {string} errorMessage rejection message on read failure
 * @returns {Promise<string>} data URL
 */
function blobToDataUrl(blob, errorMessage) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(errorMessage));
    reader.readAsDataURL(blob);
  });
}

/** Payload size of a data URL, in bytes (base64 carries 3 bytes per 4 chars). */
export function dataUrlBytes(dataUrl) {
  const base64 = String(dataUrl || '').split(',')[1] || '';
  const padding = (base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0);
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

/**
 * Read an image file, downscale it to fit maxDimension and return a
 * compressed JPEG data URL suitable for storing in the database.
 * @param {File} file
 * @param {object} [options]
 * @param {number} [options.maxDimension] longest edge of the result, in pixels
 * @param {number} [options.quality] initial JPEG quality (0-1)
 * @param {number} [options.maxBytes] when set, re-encode at lower quality
 *   until the result fits (best effort — the caller still checks the size)
 * @returns {Promise<string>} data URL
 */
export async function processImageFile(file, options = {}) {
  const {
    maxDimension = MAX_DIMENSION,
    quality = JPEG_QUALITY,
    maxBytes = 0
  } = options;

  if (!file || !file.type || !file.type.startsWith('image/')) {
    throw new Error('Please choose an image file (JPG or PNG).');
  }
  if (file.size > MAX_INPUT_BYTES) {
    throw new Error('Image is too large. Please choose a file under 20 MB.');
  }

  const sourceUrl = await blobToDataUrl(file, 'Could not read the selected file.');
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = () => reject(new Error('The selected file is not a valid image.'));
    img.onload = () => {
      try {
        const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
        const width = Math.max(1, Math.round(img.width * scale));
        const height = Math.max(1, Math.round(img.height * scale));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        // White background so transparent PNGs don't turn black as JPEG
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        let out = canvas.toDataURL('image/jpeg', quality);
        // A busy photo can still be large at the requested quality; step the
        // quality down until it fits rather than rejecting the user's image.
        if (maxBytes > 0) {
          for (const q of [0.6, 0.5, 0.4]) {
            if (dataUrlBytes(out) <= maxBytes) break;
            // Skip rungs at or above the requested quality — re-encoding
            // there only costs time and can't shrink anything.
            if (q < quality) out = canvas.toDataURL('image/jpeg', q);
          }
        }
        resolve(out);
      } catch (err) {
        reject(new Error('Could not process the image.'));
      }
    };
    img.src = sourceUrl;
  });
}

/**
 * Return a data URL for a photo reference. Data URLs pass through untouched;
 * web-app photo URLs (/api/photos/...) are fetched and inlined — needed by
 * the PDF generator, which can only embed image data, not URLs.
 * @param {string|null} src
 * @returns {Promise<string|null>}
 */
export async function ensureDataUrl(src) {
  if (!src || src.startsWith('data:')) return src;
  const res = await fetch(src);
  if (res.status === 401) {
    // Session died — let the web auth gate take over (no-op on desktop,
    // which never passes URLs here).
    window.dispatchEvent(new Event('ppm-auth-lost'));
  }
  if (!res.ok) throw new Error('Could not load photo');
  return await blobToDataUrl(await res.blob(), 'Could not read photo');
}

/**
 * Load a data-URL image and return its natural dimensions.
 * Used by the PDF generator to preserve aspect ratio.
 * @param {string} dataUrl
 * @returns {Promise<{width: number, height: number}>}
 */
export function getImageDimensions(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('Invalid image data'));
    img.src = dataUrl;
  });
}
